import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api, IShipmentStatus } from '../api/api';
import Button from './Button';
import FormInput from './FormInput';
import CustomDropdown from './CustomDropdown';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #0E1F1F;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 480px;
  border: 2px solid #284640;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #24BA7D;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Icon = styled.div`
  color: white;
  font-weight: bold;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const ModalTitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #FFFFFF;
  margin: 0;
`;

const ModalMessage = styled.p`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #838D90;
  margin: 0;
  line-height: 1.5;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputLabel = styled.label`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
  margin-bottom: 4px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #838D90;
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #284640;
  }
`;

interface ShipmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (statusId: number) => void;
  sneakerId: number;
  productName?: string;
}

const ShipmentModal: React.FC<ShipmentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  sneakerId,
  productName = 'товар'
}) => {
  const [statuses, setStatuses] = useState<IShipmentStatus[]>([]);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [newStatusName, setNewStatusName] = useState('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Загрузка статусов при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      loadStatuses();
    }
  }, [isOpen]);

  const loadStatuses = async () => {
    try {
      setIsLoading(true);
      const statusesData = await api.getShipmentStatuses();
      setStatuses(statusesData);
      setError(null);
    } catch (err) {
      console.error('Ошибка загрузки статусов отгрузки:', err);
      setError('Не удалось загрузить статусы отгрузки');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNewStatus = async () => {
    if (!newStatusName.trim()) {
      setError('Введите название статуса');
      return;
    }

    try {
      setIsLoading(true);
      const newStatus = await api.createShipmentStatus({ status: newStatusName.trim() });
      
      // Обновляем список статусов
      await loadStatuses();
      
      // Выбираем созданный статус
      setSelectedStatusId(newStatus.id);
      setNewStatusName('');
      setIsCreatingNew(false);
      setError(null);
    } catch (err) {
      console.error('Ошибка создания статуса:', err);
      setError('Не удалось создать статус отгрузки');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedStatusId) {
      setError('Выберите статус отгрузки');
      return;
    }

    try {
      setIsLoading(true);
      await api.createShipment({
        sneaker_id: sneakerId,
        status_id: selectedStatusId
      });
      
      onConfirm(selectedStatusId);
      handleClose();
    } catch (err) {
      console.error('Ошибка создания отгрузки:', err);
      setError('Не удалось создать отгрузку');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStatusId(null);
    setNewStatusName('');
    setIsCreatingNew(false);
    setError(null);
    onClose();
  };

  const statusOptions = statuses
    .filter(status => status.status.trim()) // Фильтруем пустые статусы
    .map(status => ({
      value: status.id.toString(),
      label: status.status
    }));

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <IconContainer>
            <Icon>📦</Icon>
          </IconContainer>
          <div>
            <ModalTitle>Отгрузить товар</ModalTitle>
            <ModalMessage>
              Выберите статус отгрузки для товара: {productName}
            </ModalMessage>
          </div>
        </ModalHeader>

        <FormSection>
          {!isCreatingNew ? (
            <>
              <div>
                <InputLabel>Выберите статус отгрузки:</InputLabel>
                <CustomDropdown
                  value={selectedStatusId?.toString() || ''}
                  onChange={(value) => setSelectedStatusId(parseInt(value))}
                  options={statusOptions}
                  placeholder="Выберите статус..."
                  disabled={isLoading}
                />
              </div>

              <OrDivider>или</OrDivider>

              <Button
                variant="secondary"
                onClick={() => setIsCreatingNew(true)}
                disabled={isLoading}
              >
                Создать новый статус
              </Button>
            </>
          ) : (
            <>
              <div>
                <InputLabel>Название нового статуса:</InputLabel>
                <FormInput
                  type="text"
                  value={newStatusName}
                  onChange={(e) => setNewStatusName(e.target.value)}
                  placeholder="Например: avito, wildberries..."
                  disabled={isLoading}
                />
              </div>

              <ButtonGroup>
                <Button
                  variant="primary"
                  onClick={handleCreateNewStatus}
                  disabled={isLoading || !newStatusName.trim()}
                >
                  {isLoading ? 'Создание...' : 'Создать'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsCreatingNew(false);
                    setNewStatusName('');
                  }}
                  disabled={isLoading}
                >
                  Отмена
                </Button>
              </ButtonGroup>
            </>
          )}

          {error && (
            <ModalMessage style={{ color: '#FF6B6B' }}>
              {error}
            </ModalMessage>
          )}
        </FormSection>

        {!isCreatingNew && (
          <ButtonGroup>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={isLoading || !selectedStatusId}
            >
              {isLoading ? 'Отгружается...' : 'Отгрузить'}
            </Button>
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Отмена
            </Button>
          </ButtonGroup>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default ShipmentModal;